import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { cloudSaveNames } from "./emulator/cloudSaves";
import { settleProfile } from "./emulator/profiles";
import { reloadWhenSessionEnds, session, takeSignInFromUrl } from "./auth/session";
import { settlePreferences } from "@common/preferences";
import { loadControllers } from "./emulator/controllers";
import { recordVisit } from "@jkast90/kast-common/kast-dev-api";
import { getDeploymentConfig } from "@common/deployment-config";
import { startTheme } from "@common/themePreference";
import { registerServiceWorker } from "@common/serviceWorker";
import "./index.css";

const container = document.getElementById("root")!;
// Counts this page load in the central visitor log (api/src/visitors.rs):
// someone playing without signing in calls nothing else.
recordVisit(getDeploymentConfig().apiUrl);
// So the next load opens with no network (src/service-worker.js).
registerServiceWorker();
// index.html already set the theme; this follows the OS for "system" and
// takes the account's choice once settlePreferences brings it.
startTheme();

// Back from sign-in: the tokens go into the session and out of the URL
// first. Then, before anything reads the browser's saves and settings, make
// them the signed-in account's: settleProfile claims the browser for the
// account that just signed in, or adopts what was played signed out.
async function start() {
  // Settled first: a stored session that had expired is refreshed (or
  // ended) before anyone's saves move or any call goes out on it.
  await session.ready();
  const sub = session.getState().user?.sub || null;
  // From here a session that ends by itself reloads, as a sign-out does.
  reloadWhenSessionEnds();
  await settleProfile(
    sub,
    () => {
      container.textContent = "Switching to this account's saves…";
    },
    {
      // Another tab can sign in as someone else meanwhile (the session
      // follows it): then this account's slots can't be listed, so the
      // adoption waits for the next load.
      cloudNames: async () => {
        const token = session.bearer();
        return token && session.getState().user?.sub === sub ? cloudSaveNames(token) : null;
      },
    },
  );
  await settlePreferences();
  // The shared controller profiles, read again in the background; a game
  // that opens first plays with the cached copy and rebinds when it lands.
  void loadControllers();
  ReactDOM.createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Sign-in started on the player goes back there by a full page load.
if (!takeSignInFromUrl()) void start();
